import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/context/LangContext";

const stats = {
  fr: [
    { value: 500, suffix: "+", label: "Événements réalisés" },
    { value: 35, suffix: "+", label: "Ans d'expérience" },
    { value: 50, suffix: "+", label: "Villes au Québec" },
    { value: 100, suffix: "%", label: "Satisfaction client" },
  ],
  en: [
    { value: 500, suffix: "+", label: "Events completed" },
    { value: 35, suffix: "+", label: "Years of experience" },
    { value: 50, suffix: "+", label: "Cities in Quebec" },
    { value: 100, suffix: "%", label: "Client satisfaction" },
  ],
};

function AnimatedCounter({ target, suffix, duration = 2000 }) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !started) setStarted(true); },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    const interval = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(interval);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(interval);
  }, [started, target, duration]);

  return (
    <span ref={ref}>
      {count}{suffix}
    </span>
  );
}

export default function StatsCounter() {
  const { lang } = useLang();
  const data = stats[lang] || stats.fr;

  return (
    <section className="relative py-12 md:py-20 bg-[#050505] overflow-hidden" data-testid="stats-section">
      {/* Red line accent */}
      <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#E60000]/40 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#E60000]/40 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          {data.map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15, duration: 0.6 }}
              className="text-center group"
              data-testid={`stat-${i}`}
            >
              <div className="font-heading text-4xl sm:text-5xl md:text-6xl font-black text-white mb-2 tracking-tighter">
                <AnimatedCounter target={stat.value} suffix={stat.suffix} />
              </div>
              <div className="w-8 h-[2px] bg-[#E60000] mx-auto mb-3 group-hover:w-16 transition-all duration-500" />
              <p className="font-body text-xs sm:text-sm text-[#A3A3A3] uppercase tracking-widest">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
